import type {
  AppointmentRecord,
  AppointmentStatus,
  CustomerProfile,
  PetRecord,
  VaccinationStatus,
} from "./types";

export function getInitials(fullName: string): string {
  const parts = fullName.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? "").join("");
}

export function nextUpcomingAppointment(
  appointments: AppointmentRecord[],
  nowIso: string = new Date().toISOString(),
): AppointmentRecord | undefined {
  return [...appointments]
    .filter((a) => a.startTime > nowIso && (a.status === "confirmed" || a.status === "pending"))
    .sort((a, b) => a.startTime.localeCompare(b.startTime))[0];
}

export function mostBookedService(appointments: AppointmentRecord[]): string | undefined {
  if (!appointments.length) return undefined;
  const counts = new Map<string, number>();
  for (const a of appointments) counts.set(a.serviceName, (counts.get(a.serviceName) ?? 0) + 1);
  return [...counts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0];
}

export function preferredStaffName(
  profile: CustomerProfile,
  fallback?: string,
): string | undefined {
  const explicit = profile.customer.preferredStaffId;
  if (explicit) {
    const match = profile.appointments.find((a) => a.staffId === explicit);
    if (match?.staffName) return match.staffName;
  }
  // most-frequent
  const counts = new Map<string, { id: string; name?: string; count: number }>();
  for (const a of profile.appointments) {
    const cur = counts.get(a.staffId) ?? { id: a.staffId, name: a.staffName, count: 0 };
    cur.count += 1;
    cur.name = cur.name ?? a.staffName;
    counts.set(a.staffId, cur);
  }
  const top = [...counts.values()].sort((a, b) => b.count - a.count)[0];
  return top?.name ?? fallback;
}

export function statusTone(
  status: AppointmentStatus,
): "success" | "info" | "warning" | "danger" | "muted" | "primary" {
  switch (status) {
    case "confirmed":
      return "success";
    case "in_progress":
      return "info";
    case "pending":
      return "warning";
    case "cancelled":
      return "danger";
    case "no_show":
      return "muted";
    case "completed":
      return "primary";
  }
}

export function vaccinationTone(
  status: VaccinationStatus,
): "success" | "warning" | "danger" {
  if (status === "current") return "success";
  if (status === "due_soon") return "warning";
  return "danger";
}

export function ageInYears(birthDateIso?: string, ref: Date = new Date()): number | undefined {
  if (!birthDateIso) return undefined;
  const d = new Date(birthDateIso);
  if (Number.isNaN(d.getTime())) return undefined;
  const ms = ref.getTime() - d.getTime();
  return Math.max(0, Math.floor(ms / (365.25 * 24 * 60 * 60 * 1000)));
}

export function groupTimelineByDay<T extends { occurredAt: string }>(
  events: T[],
): Array<{ dayKey: string; date: Date; items: T[] }> {
  const map = new Map<string, { date: Date; items: T[] }>();
  for (const e of events) {
    const d = new Date(e.occurredAt);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const cur = map.get(key) ?? { date: new Date(d.getFullYear(), d.getMonth(), d.getDate()), items: [] };
    cur.items.push(e);
    map.set(key, cur);
  }
  return [...map.entries()]
    .map(([dayKey, v]) => ({ dayKey, ...v }))
    .sort((a, b) => b.dayKey.localeCompare(a.dayKey));
}

export function formatDateShort(iso: string, locale = "tr-TR"): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(locale, { day: "2-digit", month: "short", year: "numeric" });
}

export function formatTimeShort(iso: string, locale = "tr-TR"): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
}

export function formatMoney(amount: number, currency = "TRY", locale = "tr-TR"): string {
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${amount} ${currency}`;
  }
}

export function relativeDaysFromNow(iso: string, ref: Date = new Date()): number {
  const d = new Date(iso);
  const startA = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate()).getTime();
  const startB = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  return Math.round((startA - startB) / (24 * 60 * 60 * 1000));
}

export function petsById(pets: PetRecord[]): Map<string, PetRecord> {
  return new Map(pets.map((p) => [p.id, p]));
}
